import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, useToast } from '@/components/ui';
import { isApiError } from '@/lib/api';
import { useOwnSessions, useRevokeOwnSession } from './api';
import { RevokeSessionDialog } from './RevokeSessionDialog';
import { SessionList } from './SessionList';
import styles from './StaffSessions.module.css';
import type { StaffSession } from './types';

export function SignedInDevicesDrawer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const sessions = useOwnSessions();
  const revoke = useRevokeOwnSession();
  const [selected, setSelected] = useState<StaffSession | null>(null);

  async function confirm() {
    if (!selected) return;
    try {
      await revoke.mutateAsync(selected.id);
      toast.show({ tone: 'success', title: t('staffSessions.signedOutToast') });
      setSelected(null);
    } catch (error) {
      if (isApiError(error) && error.code === 'CURRENT_SESSION') {
        await sessions.refetch();
      }
    }
  }

  return (
    <Dialog
      open
      title={t('staffSessions.signedInDevices')}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      <p className={styles.intro}>{t('staffSessions.ownIntro')}</p>
      <SessionList
        sessions={sessions.data}
        loading={sessions.isLoading}
        error={sessions.error}
        onRetry={() => void sessions.refetch()}
        onRevoke={setSelected}
        revokingId={revoke.isPending ? selected?.id : undefined}
      />
      {selected && (
        <RevokeSessionDialog
          target={selected.device}
          requiresReason={false}
          pending={revoke.isPending}
          error={revoke.error}
          onClose={() => {
            revoke.reset();
            setSelected(null);
          }}
          onConfirm={() => void confirm()}
        />
      )}
    </Dialog>
  );
}
